import { useState } from 'react';
import type { JobFilters as JobFiltersType } from '../../../types/job';

interface JobFiltersProps {
  filters: JobFiltersType;
  onFiltersChange: (filters: Partial<JobFiltersType>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export function JobFilters({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  hasActiveFilters 
}: JobFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const jobTypes = [
    { value: 'full-time', label: 'Full-time' },
    { value: 'part-time', label: 'Part-time' },
    { value: 'contract', label: 'Contract' },
    { value: 'remote', label: 'Remote' },
    { value: 'internship', label: 'Internship' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level' },
    { value: 'mid', label: 'Mid Level' },
    { value: 'senior', label: 'Senior Level' },
    { value: 'lead', label: 'Lead' },
    { value: 'executive', label: 'Executive' }
  ];

  const postedWithinOptions = [
    { value: 'all', label: 'Any time' },
    { value: 'day', label: 'Past 24 hours' },
    { value: 'week', label: 'Past week' },
    { value: 'month', label: 'Past month' }
  ];

  const salaryRanges = [
    { value: [0, 50000], label: 'Under INR 50k' },
    { value: [50000, 75000], label: 'INR 50k - 75k' },
    { value: [75000, 100000], label: 'INR 75k - 100k' },
    { value: [100000, 150000], label: 'INR 100k - 150k' },
    { value: [150000, 200000], label: 'INR 150k - 200k' },
    { value: [200000, 999999], label: 'INR 200k+' }
  ];

  const handleJobTypeChange = (type: string, checked: boolean) => {
    const currentTypes = filters.jobType || [];
    const newTypes = checked
      ? [...currentTypes, type]
      : currentTypes.filter(t => t !== type);
    
    onFiltersChange({ jobType: newTypes.length > 0 ? newTypes : undefined });
  };

  const handleExperienceChange = (level: string, checked: boolean) => {
    const currentLevels = filters.experience || [];
    const newLevels = checked
      ? [...currentLevels, level]
      : currentLevels.filter(l => l !== level);
    
    onFiltersChange({ experience: newLevels.length > 0 ? newLevels : undefined });
  };

  const handleSalaryRangeChange = (range: [number, number] | null) => {
    onFiltersChange({ salaryRange: range || undefined });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Mobile Toggle */}
      <div className="lg:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between text-left bg-gray-50 border-b border-gray-200"
        >
          <span className="font-semibold text-gray-900">Filters</span>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                Active
              </span>
            )}
            <svg 
              className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
      </div>

      {/* Filters Content */}
      <div className={`${isExpanded ? 'block' : 'hidden'} lg:block`}>
        {/* Header */}
        <div className="hidden lg:block px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Filters</h3>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Location Filter */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-900 mb-2">
              Location
            </label>
            <input
              type="text"
              id="location"
              value={filters.location || ''}
              onChange={(e) => onFiltersChange({ location: e.target.value || undefined })}
              placeholder="City, state, or remote"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          {/* Company Filter */}
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
              Company
            </label>
            <input
              type="text"
              id="company"
              value={filters.company || ''}
              onChange={(e) => onFiltersChange({ company: e.target.value || undefined })}
              placeholder="Company name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          {/* Job Type Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Job Type</h4>
            <div className="space-y-2">
              {jobTypes.map((type) => (
                <label key={type.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.jobType?.includes(type.value) || false}
                    onChange={(e) => handleJobTypeChange(type.value, e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Experience Level Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Experience Level</h4>
            <div className="space-y-2">
              {experienceLevels.map((level) => (
                <label key={level.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.experience?.includes(level.value) || false}
                    onChange={(e) => handleExperienceChange(level.value, e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{level.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Salary Range Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Salary Range</h4>
            <div className="space-y-2">
              {salaryRanges.map((range, index) => (
                <label key={index} className="flex items-center">
                  <input
                    type="radio"
                    name="salaryRange"
                    checked={
                      filters.salaryRange?.[0] === range.value[0] && 
                      filters.salaryRange?.[1] === range.value[1]
                    }
                    onChange={() => handleSalaryRangeChange(range.value as [number, number])}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">{range.label}</span>
                </label>
              ))}
              <label className="flex items-center">
                <input
                  type="radio"
                  name="salaryRange"
                  checked={!filters.salaryRange}
                  onChange={() => handleSalaryRangeChange(null)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Any salary</span>
              </label>
            </div>
          </div>

          {/* Posted Within Filter */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Posted Within</h4>
            <div className="space-y-2">
              {postedWithinOptions.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="postedWithin"
                    value={option.value}
                    checked={filters.postedWithin === option.value || (!filters.postedWithin && option.value === 'all')}
                    onChange={(e) => onFiltersChange({ 
                      postedWithin: e.target.value === 'all' ? undefined : e.target.value as JobFiltersType['postedWithin']
                    })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear Filters Button (Mobile) */}
          {hasActiveFilters && (
            <div className="lg:hidden pt-4 border-t border-gray-200">
              <button
                onClick={onClearFilters}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}